import { ReactElement, useMemo, useState } from 'react'
import { uniq } from 'lodash-es'
import { Box, Button, Collapse, Stack, Typography, useTheme } from '@mui/material'
import { SnackbarContent, closeSnackbar } from '@ui/snackbar'

type Props = {
  snackbarKey: string | number
  summaryMessage: string
  fullMessage: string
  actionableGuids: readonly string[]
  guidToLabel: Record<string, string>
  onAction: (guid: string) => void
}

const CalculateErrorSnackbarContent = ({
  snackbarKey,
  summaryMessage,
  fullMessage,
  actionableGuids,
  guidToLabel,
  onAction,
}: Props): ReactElement => {
  const [detailsOpen, setDetailsOpen] = useState(false)
  const theme = useTheme()

  const actionableGuidsCleaned = useMemo(
    () =>
      uniq(
        actionableGuids.map(guid => {
          // For now we only want to allow selecting the elemenets that are part of the model mesh
          // as the slab-beams are also hidden by default and they are not visible in other views.
          // Once they are part of the modelmesh/global scene we can think about allowing to select
          // them as well.
          if (guid.startsWith('beam-of-') || guid.startsWith('rip-of-')) {
            return guid.split('-').slice(2, undefined).join('-')
          }
          return guid
        }),
      ),
    [actionableGuids],
  )

  return (
    <SnackbarContent
      style={{
        flexWrap: 'wrap',
        alignItems: 'flex-start',
        // backgroundColor: theme.palette.error.dark,
        color: theme.palette.error.contrastText,
      }}
    >
      <Stack spacing={1} sx={{ maxWidth: 560 }}>
        <Collapse in collapsedSize={0}>
          <Typography variant="body2" component="div">
            {summaryMessage}
          </Typography>
        </Collapse>
        <Collapse in={detailsOpen} unmountOnExit sx={{ backgroundColor: theme.palette.error.dark }}>
          <Box sx={{ padding: 2 }}>
            <Typography
              variant="body2"
              component="div"
              sx={{
                wordBreak: 'break-word',
                whiteSpace: 'pre-wrap',
                maxHeight: 220,
                overflow: 'auto',
                pr: 0.5,
              }}
            >
              {fullMessage}
            </Typography>
          </Box>
        </Collapse>
        <Stack direction="row" flexWrap="wrap" gap={0.5} justifyContent="flex-end" width="100%">
          <Button
            color="inherit"
            size="small"
            onClick={() => setDetailsOpen(open => !open)}
            sx={{ textTransform: 'none' }}
          >
            {detailsOpen ? 'Weniger' : 'Mehr'}
          </Button>
          {actionableGuidsCleaned.map(guid => {
            const labelText = guidToLabel[guid] || guid

            return (
              <Button
                key={guid}
                color="inherit"
                size="small"
                sx={{ textTransform: 'none' }}
                onClick={() => {
                  onAction(guid)
                  closeSnackbar(snackbarKey)
                }}
              >
                {guidToLabel[guid] ? `${labelText} anzeigen` : 'Element anzeigen'}
              </Button>
            )
          })}
          <Button
            color="inherit"
            size="small"
            sx={{ textTransform: 'none' }}
            onClick={() => closeSnackbar(snackbarKey)}
          >
            OK
          </Button>
        </Stack>
      </Stack>
    </SnackbarContent>
  )
}

export default CalculateErrorSnackbarContent
